import React, { useState, useEffect } from 'react';
import {
  Search,
  RefreshCw,
  Tag,
  ExternalLink,
  Edit2,
  Trash2,
  Settings,
  FileText,
  FileSpreadsheet,
  Presentation,
  FileCode,
  File,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  Coins,
  User,
  Globe
} from 'lucide-react';
import { Card } from '../ui/Card';
import { getAuthHeaders, customFetch } from '../../services/apiService';
import { AuthorAsyncAutocomplete } from './AuthorAsyncAutocomplete';

export interface LibraryDocument {
  id: string;
  driveFileId: string;
  name: string;
  description: string;
  author?: string;
  authorId?: string | null;
  source?: string;
  fileType: 'pdf' | 'docx' | 'pptx' | 'xlsx' | 'zip' | 'other';
  driveUrl: string;
  viewLink: string;
  gradeLevel: string;
  subject: string;
  creditCost: number;
  downloadCount: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const LibraryManagement: React.FC = () => {
  // Filters state
  const getInitialPage = () => {
    if (typeof window === 'undefined') return 1;
    const searchParams = new URLSearchParams(window.location.search);
    return parseInt(searchParams.get('page') || '1', 10) || 1;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState<'all' | 'free' | 'paid'>('all');
  const [page, setPage] = useState(getInitialPage);

  // Sync page to browser URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      window.history.replaceState(null, '', url.toString());
    }
  }, [page]);

  // Data state
  const [documents, setDocuments] = useState<LibraryDocument[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Modals & Inline Edit state
  const [editingPriceDocId, setEditingPriceDocId] = useState<string | null>(null);
  const [inlinePriceValue, setInlinePriceValue] = useState<number>(0);

  const [configModalDoc, setConfigModalDoc] = useState<LibraryDocument | null>(null);
  const [selectedAuthorId, setSelectedAuthorId] = useState<string | null>(null);
  const [configFormData, setConfigFormData] = useState({
    name: '',
    description: '',
    author: 'Nhà Giáo Số',
    source: 'Nhà Giáo Số',
    creditCost: 0,
    gradeLevel: 'Lớp 4',
    subject: 'Toán',
    isPublic: true,
  });
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // Bulk Edit Modal State
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkSelectedAuthorId, setBulkSelectedAuthorId] = useState<string | null>(null);
  const [bulkFormData, setBulkFormData] = useState({
    updateAuthor: false,
    author: 'Nhà Giáo Số',
    updateSource: false,
    source: 'Nhà Giáo Số',
    updatePrice: false,
    creditCost: 0,
  });
  const [isSavingBulk, setIsSavingBulk] = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Fetch Documents
  const fetchDocuments = async (pageNumber = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNumber.toString(),
        limit: '10',
        search: searchTerm,
        gradeLevel: gradeFilter,
        subject: subjectFilter,
        isPaid: priceFilter,
      });

      const res = await customFetch(`/api/admin/library/documents?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const docsList = data.documents || data.data?.documents || [];
        const pag = data.pagination || data.data?.pagination || {
          page: pageNumber,
          limit: 10,
          totalCount: docsList.length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        };
        setDocuments(docsList);
        setPagination(pag);
      } else {
        console.error('Failed to fetch library documents');
      }
    } catch (err) {
      console.error('Lỗi kết nối khi tải danh sách tài liệu:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments(1);
    setPage(1);
  }, [searchTerm, gradeFilter, subjectFilter, priceFilter]);

  useEffect(() => {
    fetchDocuments(page);
  }, [page]);

  // Action: Trigger Google Drive Sync
  const handleSyncDrive = async () => {
    setIsSyncing(true);
    setToastMessage("Đang tiến hành đồng bộ dữ liệu từ Google Drive...");
    try {
      const res = await customFetch('/api/admin/library/sync-drive', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setToastMessage(data.message || "Đồng bộ thành công dữ liệu từ Google Drive!");
        fetchDocuments(page);
      } else {
        setToastMessage("Đã xảy ra lỗi khi đồng bộ Google Drive.");
      }
    } catch (err) {
      console.error('Error syncing Drive:', err);
      setToastMessage("Lỗi kết nối server khi đồng bộ Google Drive.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Select all checkboxes on current page
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allCurrentIds = documents.map(d => d.id);
      setSelectedIds(Array.from(new Set([...selectedIds, ...allCurrentIds])));
    } else {
      const currentIdsSet = new Set(documents.map(d => d.id));
      setSelectedIds(selectedIds.filter(id => !currentIdsSet.has(id)));
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(item => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const isAllCurrentSelected =
    documents.length > 0 && documents.every(d => selectedIds.includes(d.id));

  // Inline Price Edit
  const handleStartInlineEdit = (doc: LibraryDocument) => {
    setEditingPriceDocId(doc.id);
    setInlinePriceValue(doc.creditCost || 0);
  };

  const handleSaveInlinePrice = async (docId: string) => {
    try {
      const res = await customFetch(`/api/admin/library/documents/${docId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ creditCost: Math.max(0, Number(inlinePriceValue) || 0) }),
      });
      if (res.ok) {
        setEditingPriceDocId(null);
        fetchDocuments(page);
        setToastMessage("Đã cập nhật giá Xu thành công!");
      } else {
        alert("Không thể lưu giá Xu.");
      }
    } catch (err) {
      console.error('Lỗi khi cập nhật giá:', err);
    }
  };

  // Toggle Public status
  const handleTogglePublic = async (doc: LibraryDocument) => {
    try {
      const res = await customFetch(`/api/admin/library/documents/${doc.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isPublic: !doc.isPublic }),
      });
      if (res.ok) {
        fetchDocuments(page);
        setToastMessage(doc.isPublic ? "Đã ẩn tài liệu đối với người dùng" : "Đã bật công khai tài liệu!");
      }
    } catch (err) {
      console.error('Lỗi đổi trạng thái công khai:', err);
    }
  };

  // Delete document
  const handleDeleteDoc = async (doc: LibraryDocument) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tài liệu "${doc.name}" khỏi hệ thống?`)) {
      return;
    }
    try {
      const res = await customFetch(`/api/admin/library/documents/${doc.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setToastMessage("Đã xóa tài liệu khỏi hệ thống!");
        fetchDocuments(page);
        setSelectedIds(selectedIds.filter(id => id !== doc.id));
      } else {
        alert("Xóa thất bại. Vui lòng thử lại.");
      }
    } catch (err) {
      console.error('Lỗi khi xóa tài liệu:', err);
    }
  };

  // Open Config Modal
  const handleOpenConfigModal = (doc: LibraryDocument) => {
    setConfigModalDoc(doc);
    setSelectedAuthorId(doc.authorId || null);
    setConfigFormData({
      name: doc.name,
      description: doc.description || '',
      author: doc.author || 'Nhà Giáo Số',
      source: doc.source || 'Nhà Giáo Số',
      creditCost: doc.creditCost || 0,
      gradeLevel: doc.gradeLevel || 'Lớp 4',
      subject: doc.subject || 'Toán',
      isPublic: doc.isPublic ?? true,
    });
  };

  // Submit Config Modal
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configModalDoc) return;

    setIsSavingConfig(true);
    try {
      const res = await customFetch(`/api/admin/library/documents/${configModalDoc.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: configFormData.name,
          description: configFormData.description,
          author: configFormData.author.trim() || 'Nhà Giáo Số',
          authorId: selectedAuthorId || null,
          source: configFormData.source.trim() || 'Nhà Giáo Số',
          creditCost: Math.max(0, Number(configFormData.creditCost) || 0),
          gradeLevel: configFormData.gradeLevel,
          subject: configFormData.subject,
          isPublic: configFormData.isPublic,
        }),
      });

      if (res.ok) {
        setConfigModalDoc(null);
        setToastMessage("Đã lưu cài đặt cấu hình tài liệu thành công!");
        fetchDocuments(page);
      } else {
        alert("Không thể lưu cấu hình.");
      }
    } catch (err) {
      console.error('Lỗi lưu cấu hình:', err);
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Submit Bulk Edit Update
  const handleSaveBulkEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;

    if (!bulkFormData.updateAuthor && !bulkFormData.updateSource && !bulkFormData.updatePrice) {
      alert("Vui lòng chọn ít nhất một trường dữ liệu cần cập nhật (Tác giả, Nguồn hoặc Giá Xu).");
      return;
    }

    setIsSavingBulk(true);
    try {
      const payload: any = {
        documentIds: selectedIds,
        updateAuthor: bulkFormData.updateAuthor,
        updateSource: bulkFormData.updateSource,
        updatePrice: bulkFormData.updatePrice,
      };

      if (bulkFormData.updateAuthor) {
        payload.updateAuthorId = true;
        payload.authorId = bulkSelectedAuthorId || null;
        payload.author = bulkFormData.author.trim() || 'Nhà Giáo Số';
      }
      if (bulkFormData.updateSource) {
        payload.source = bulkFormData.source.trim() || 'Nhà Giáo Số';
      }
      if (bulkFormData.updatePrice) {
        payload.creditCost = Math.max(0, Number(bulkFormData.creditCost) || 0);
      }

      const res = await customFetch('/api/admin/library/documents/bulk-update', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        setShowBulkEditModal(false);
        setBulkSelectedAuthorId(null);
        setToastMessage(data.message || "Đã cập nhật tài liệu hàng loạt thành công!");
        fetchDocuments(page);
      } else {
        alert("Không thể cập nhật hàng loạt.");
      }
    } catch (err) {
      console.error('Lỗi sửa tài liệu hàng loạt:', err);
    } finally {
      setIsSavingBulk(false);
    }
  };

  // Icon selector based on fileType or extension
  const getFileIcon = (fileType: string, name: string) => {
    const ext = name.split('.').pop()?.toLowerCase() || '';
    if (fileType === 'pdf' || ext === 'pdf') {
      return <FileText className="w-5 h-5 text-rose-500 shrink-0" />;
    }
    if (fileType === 'docx' || ext === 'doc' || ext === 'docx') {
      return <FileText className="w-5 h-5 text-blue-600 shrink-0" />;
    }
    if (fileType === 'pptx' || ext === 'ppt' || ext === 'pptx') {
      return <Presentation className="w-5 h-5 text-amber-600 shrink-0" />;
    }
    if (fileType === 'xlsx' || ext === 'xls' || ext === 'xlsx') {
      return <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />;
    }
    return <File className="w-5 h-5 text-slate-400 shrink-0" />;
  };

  return (
    <div className="space-y-6">
      {/* Toast notification banner */}
      {toastMessage && (
        <div className="bg-purple-900 text-white p-3.5 px-5 rounded-2xl shadow-lg flex items-center justify-between animate-fade-in border border-purple-700">
          <div className="flex items-center space-x-3">
            <RefreshCw className={`w-5 h-5 text-purple-300 ${isSyncing ? 'animate-spin' : ''}`} />
            <span className="text-xs font-bold">{toastMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="text-purple-300 hover:text-white p-1 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Container Card */}
      <Card>
        <div className="space-y-5">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div>
              <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <span>Quản Lý Thư Viện Tài Liệu</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-extrabold uppercase">
                  Google Drive Auto-Sync
                </span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Tài liệu được hệ thống quét và lưu tự động từ Google Drive API. Đặt giá Xu và quản lý hiển thị cho giáo viên.
              </p>
            </div>

            {/* Top Toolbar Action Buttons */}
            <div className="flex items-center space-x-2 shrink-0">
              {/* Bulk Edit Button */}
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setBulkSelectedAuthorId(null);
                    setBulkFormData({
                      updateAuthor: false,
                      author: 'Nhà Giáo Số',
                      updateSource: false,
                      source: 'Nhà Giáo Số',
                      updatePrice: false,
                      creditCost: 0,
                    });
                    setShowBulkEditModal(true);
                  }}
                  className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 active:scale-95 animate-fade-in"
                >
                  <Tag className="w-4 h-4" />
                  <span>Sửa Hàng Loạt ({selectedIds.length})</span>
                </button>
              )}

              {/* Sync Google Drive Button */}
              <button
                type="button"
                disabled={isSyncing}
                onClick={handleSyncDrive}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-2 disabled:opacity-50 active:scale-95"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>Đồng bộ Google Drive</span>
              </button>
            </div>
          </div>

          {/* Search & Filters Toolbar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-200/80">
            {/* 1. Search Box */}
            <div className="lg:col-span-4 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Tìm tên, mô tả hoặc Mã Drive File ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* 2. Grade Level Filter */}
            <div className="lg:col-span-3">
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tất cả khối lớp</option>
                <option value="Lớp 1">Lớp 1</option>
                <option value="Lớp 2">Lớp 2</option>
                <option value="Lớp 3">Lớp 3</option>
                <option value="Lớp 4">Lớp 4</option>
                <option value="Lớp 5">Lớp 5</option>
                <option value="Khác">Khác / Khối chung</option>
              </select>
            </div>

            {/* 3. Subject Filter */}
            <div className="lg:col-span-3">
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tất cả môn học</option>
                <option value="Toán">Toán</option>
                <option value="Tiếng Việt">Tiếng Việt</option>
                <option value="Tiếng Anh">Tiếng Anh</option>
                <option value="Khoa học">Khoa học</option>
                <option value="Lịch sử & Địa lý">Lịch sử & Địa lý</option>
                <option value="Đạo đức">Đạo đức</option>
                <option value="Hoạt động trải nghiệm">Hoạt động trải nghiệm</option>
                <option value="Khác">Khác</option>
              </select>
            </div>

            {/* 4. Price Filter */}
            <div className="lg:col-span-2">
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value as any)}
                className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tất cả giá</option>
                <option value="free">Miễn phí (0 Xu)</option>
                <option value="paid">Bán Xu (&gt; 0 Xu)</option>
              </select>
            </div>
          </div>

          {/* Table Area */}
          {loading ? (
            <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin mb-3 text-purple-600" />
              <span className="text-xs font-semibold">Đang tải danh sách tài liệu từ database...</span>
            </div>
          ) : documents.length === 0 ? (
            <div className="p-12 text-center text-slate-400 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col items-center justify-center">
              <AlertCircle className="w-8 h-8 text-slate-300 mb-2" />
              <p className="text-xs font-bold text-slate-600">Không tìm thấy tài liệu phù hợp.</p>
              <p className="text-[11px] text-slate-400 mt-1">Thử thay đổi từ khóa tìm kiếm hoặc bấm "Đồng bộ Google Drive".</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-2xl bg-white shadow-2xs">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200 uppercase tracking-wider">
                  <tr>
                    <th className="p-3.5 pl-4 w-10">
                      <input
                        type="checkbox"
                        checked={isAllCurrentSelected}
                        onChange={handleSelectAll}
                        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                    </th>
                    <th className="p-3.5">Tên Tài Liệu & Link Drive</th>
                    <th className="p-3.5">Phân Loại</th>
                    <th className="p-3.5 text-center">Lượt Tải</th>
                    <th className="p-3.5 text-center">Giá Tải (Xu)</th>
                    <th className="p-3.5 text-center">Trạng Thái</th>
                    <th className="p-3.5 text-right pr-4">Hành Động</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {documents.map((doc) => {
                    const isSelected = selectedIds.includes(doc.id);
                    const isEditingPrice = editingPriceDocId === doc.id;

                    return (
                      <tr
                        key={doc.id}
                        className={`hover:bg-slate-50/80 transition-colors ${
                          isSelected ? 'bg-purple-50/40' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="p-3.5 pl-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOne(doc.id)}
                            className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                          />
                        </td>

                        {/* Name & Drive Link */}
                        <td className="p-3.5 max-w-md">
                          <div className="flex items-start space-x-3">
                            <div className="mt-0.5 p-1.5 bg-slate-100 rounded-lg shrink-0">
                              {getFileIcon(doc.fileType, doc.name)}
                            </div>
                            <div className="min-w-0">
                              <a
                                href={doc.driveUrl || doc.viewLink || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-extrabold text-slate-900 hover:text-purple-600 transition-colors line-clamp-1 flex items-center space-x-1"
                              >
                                <span>{doc.name}</span>
                                <ExternalLink className="w-3 h-3 text-slate-400 shrink-0 ml-1 inline" />
                              </a>
                              <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                                {doc.description || 'Chưa có mô tả'}
                              </p>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                  ID: {doc.driveFileId || doc.id}
                                </span>
                                <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                                  <User className="w-2.5 h-2.5 text-purple-500 shrink-0" />
                                  <span>Tác giả: <strong>{doc.author || 'Nhà Giáo Số'}</strong></span>
                                  {doc.authorId && (
                                    <span className="ml-0.5 px-1 py-0.2 bg-purple-200 text-purple-800 rounded font-bold text-[9px]">
                                      TK liên kết
                                    </span>
                                  )}
                                </span>
                                <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                                  <Globe className="w-2.5 h-2.5 text-blue-500 shrink-0" />
                                  <span>Nguồn: <strong>{doc.source || 'Nhà Giáo Số'}</strong></span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Grade & Subject Tag */}
                        <td className="p-3.5 whitespace-nowrap">
                          <span className="px-2.5 py-1 rounded-lg text-[11px] font-extrabold bg-slate-100 text-slate-700 border border-slate-200/80 inline-block">
                            {doc.gradeLevel} • {doc.subject}
                          </span>
                        </td>

                        {/* Download count */}
                        <td className="p-3.5 text-center font-bold text-slate-700 whitespace-nowrap">
                          {doc.downloadCount || 0}
                        </td>

                        {/* Credit Cost Inline Edit */}
                        <td className="p-3.5 text-center whitespace-nowrap">
                          {isEditingPrice ? (
                            <div className="flex items-center justify-center space-x-1">
                              <input
                                type="number"
                                min="0"
                                value={inlinePriceValue}
                                onChange={(e) => setInlinePriceValue(Number(e.target.value))}
                                className="w-16 py-1 px-1.5 text-center text-xs font-extrabold border border-purple-400 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-600"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveInlinePrice(doc.id)}
                                className="p-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-2xs"
                                title="Lưu giá Xu"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingPriceDocId(null)}
                                className="p-1 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300"
                                title="Hủy"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center space-x-1.5 group">
                              {doc.creditCost === 0 ? (
                                <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200 inline-block">
                                  Miễn phí (0 Xu)
                                </span>
                              ) : (
                                <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center space-x-1">
                                  <Coins className="w-3 h-3 text-amber-600" />
                                  <span>{doc.creditCost} Xu</span>
                                </span>
                              )}

                              <button
                                type="button"
                                onClick={() => handleStartInlineEdit(doc)}
                                className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                                title="Sửa giá nhanh"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Status Toggle */}
                        <td className="p-3.5 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleTogglePublic(doc)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold transition-all flex items-center space-x-1 mx-auto cursor-pointer ${
                              doc.isPublic
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                                : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                            }`}
                          >
                            {doc.isPublic ? (
                              <>
                                <Eye className="w-3 h-3 text-emerald-600" />
                                <span>Công khai</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 text-slate-400" />
                                <span>Đang ẩn</span>
                              </>
                            )}
                          </button>
                        </td>

                        {/* Action Buttons */}
                        <td className="p-3.5 text-right pr-4 whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-1.5">
                            {/* Settings Modal Button */}
                            <button
                              type="button"
                              onClick={() => handleOpenConfigModal(doc)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-purple-50 text-slate-700 hover:text-purple-700 font-bold text-xs rounded-xl transition-all border border-slate-200 flex items-center space-x-1"
                              title="Cài đặt cấu hình"
                            >
                              <Settings className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Cài đặt</span>
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => handleDeleteDoc(doc)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                              title="Xóa tài liệu"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && documents.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs">
              <div className="text-slate-500 font-medium">
                Trang <strong className="text-slate-900">{pagination.page}</strong> / <strong className="text-slate-900">{pagination.totalPages}</strong> (Tổng <strong>{pagination.totalCount}</strong> tài liệu)
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setPage(page - 1)}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Trang trước</span>
                </button>

                <button
                  type="button"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setPage(page + 1)}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <span>Trang sau</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ============================================================ */}
      {/* MODAL 1: CÀI ĐẶT CẤU HÌNH TÀI LIỆU                           */}
      {/* ============================================================ */}
      {configModalDoc && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 space-y-5 relative">
            <button
              type="button"
              onClick={() => setConfigModalDoc(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1 rounded-xl"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <h3 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <Settings className="w-5 h-5 text-purple-600" />
                <span>Cài Đặt Cấu Hình Tài Liệu</span>
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Chỉnh sửa thông tin cho file Drive ID:{' '}
                <strong className="font-mono text-purple-700">{configModalDoc.driveFileId || configModalDoc.id}</strong>
              </p>
            </div>

            <form onSubmit={handleSaveConfig} className="space-y-4">
              {/* Tên hiển thị */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Tên hiển thị tài liệu <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={configFormData.name}
                  onChange={(e) => setConfigFormData({ ...configFormData, name: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Mô tả tài liệu */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Mô tả vắn tắt tài liệu
                </label>
                <textarea
                  rows={2}
                  value={configFormData.description}
                  onChange={(e) => setConfigFormData({ ...configFormData, description: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Mô tả nội dung tài liệu..."
                />
              </div>

              {/* Grid 2 cột: Tác giả & Nguồn */}
              <div className="grid grid-cols-2 gap-3">
                {/* Tác giả (Async Autocomplete) */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center space-x-1">
                    <User className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <span>Tác giả</span>
                  </label>
                  <AuthorAsyncAutocomplete
                    value={configFormData.author}
                    authorId={selectedAuthorId}
                    onChange={(newAuthor, newAuthorId) => {
                      setConfigFormData(prev => ({ ...prev, author: newAuthor }));
                      setSelectedAuthorId(newAuthorId);
                    }}
                    placeholder="Mặc định: Nhà Giáo Số"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">
                    Gõ tìm tài khoản hoặc nhập tên tự do
                  </span>
                </div>

                {/* Nguồn */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center space-x-1">
                    <Globe className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span>Nguồn tài liệu</span>
                  </label>
                  <input
                    type="text"
                    value={configFormData.source}
                    onChange={(e) => setConfigFormData({ ...configFormData, source: e.target.value })}
                    className="w-full px-3 py-2 text-xs font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Mặc định: Nhà Giáo Số"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Mặc định: Nhà Giáo Số</span>
                </div>
              </div>

              {/* Grid 2 cột: Giá Xu & Khối Lớp */}
              <div className="grid grid-cols-2 gap-3">
                {/* Giá Xu */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Giá Tải (Xu)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={configFormData.creditCost}
                    onChange={(e) => setConfigFormData({ ...configFormData, creditCost: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs font-extrabold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Nhập 0 để đặt Miễn phí</span>
                </div>

                {/* Khối Lớp */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Khối lớp
                  </label>
                  <select
                    value={configFormData.gradeLevel}
                    onChange={(e) => setConfigFormData({ ...configFormData, gradeLevel: e.target.value })}
                    className="w-full px-3 py-2 text-xs font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Lớp 1">Lớp 1</option>
                    <option value="Lớp 2">Lớp 2</option>
                    <option value="Lớp 3">Lớp 3</option>
                    <option value="Lớp 4">Lớp 4</option>
                    <option value="Lớp 5">Lớp 5</option>
                    <option value="Khác">Khác / Khối chung</option>
                  </select>
                </div>
              </div>

              {/* Môn học */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Môn học
                </label>
                <select
                  value={configFormData.subject}
                  onChange={(e) => setConfigFormData({ ...configFormData, subject: e.target.value })}
                  className="w-full px-3 py-2 text-xs font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Toán">Toán</option>
                  <option value="Tiếng Việt">Tiếng Việt</option>
                  <option value="Tiếng Anh">Tiếng Anh</option>
                  <option value="Khoa học">Khoa học</option>
                  <option value="Lịch sử & Địa lý">Lịch sử & Địa lý</option>
                  <option value="Đạo đức">Đạo đức</option>
                  <option value="Hoạt động trải nghiệm">Hoạt động trải nghiệm</option>
                  <option value="Khác">Khác</option>
                </select>
              </div>

              {/* Toggle Public */}
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 flex items-center justify-between">
                <div>
                  <span className="text-xs font-extrabold text-slate-800 block">Công khai cho Người dùng</span>
                  <span className="text-[11px] text-slate-400">Cho phép giáo viên tìm thấy và tải tài liệu này</span>
                </div>
                <input
                  type="checkbox"
                  checked={configFormData.isPublic}
                  onChange={(e) => setConfigFormData({ ...configFormData, isPublic: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setConfigModalDoc(null)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSavingConfig}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center space-x-1.5"
                >
                  {isSavingConfig ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Đang lưu...</span>
                    </>
                  ) : (
                    <span>Lưu Cấu Hình</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 2: SỬA HÀNG LOẠT (TÁC GIẢ, NGUỒN, GIÁ XU)              */}
      {/* ============================================================ */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5 relative">
            <button
              type="button"
              onClick={() => setShowBulkEditModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1 rounded-xl"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <h3 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <Tag className="w-5 h-5 text-amber-500" />
                <span>Chỉnh Sửa Hàng Loạt Tài Liệu</span>
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Cập nhật thông tin tác giả, nguồn hoặc giá Xu cho <strong className="text-purple-700">{selectedIds.length} tài liệu</strong> đã chọn.
              </p>
            </div>

            <form onSubmit={handleSaveBulkEdit} className="space-y-4">
              {/* Option 1: Cập nhật Tác giả */}
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkFormData.updateAuthor}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, updateAuthor: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-800 flex items-center space-x-1">
                    <User className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <span>Cập nhật Tác giả hàng loạt</span>
                  </span>
                </label>
                {bulkFormData.updateAuthor && (
                  <div className="pt-1">
                    <AuthorAsyncAutocomplete
                      value={bulkFormData.author}
                      authorId={bulkSelectedAuthorId}
                      onChange={(newAuthor, newAuthorId) => {
                        setBulkFormData((prev) => ({ ...prev, author: newAuthor }));
                        setBulkSelectedAuthorId(newAuthorId);
                      }}
                      placeholder="Mặc định: Nhà Giáo Số"
                    />
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      Gõ tìm tài khoản hoặc nhập tên tự do
                    </span>
                  </div>
                )}
              </div>

              {/* Option 2: Cập nhật Nguồn */}
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkFormData.updateSource}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, updateSource: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-800 flex items-center space-x-1">
                    <Globe className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span>Cập nhật Nguồn tài liệu hàng loạt</span>
                  </span>
                </label>
                {bulkFormData.updateSource && (
                  <div className="pt-1">
                    <input
                      type="text"
                      required={bulkFormData.updateSource}
                      value={bulkFormData.source}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, source: e.target.value })}
                      className="w-full px-3 py-2 text-xs font-semibold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="Mặc định: Nhà Giáo Số"
                    />
                  </div>
                )}
              </div>

              {/* Option 3: Cập nhật Giá Tải (Xu) */}
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkFormData.updatePrice}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, updatePrice: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-800 flex items-center space-x-1">
                    <Coins className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>Cập nhật Giá Tải (Xu) hàng loạt</span>
                  </span>
                </label>
                {bulkFormData.updatePrice && (
                  <div className="pt-1 relative">
                    <Coins className="w-4 h-4 text-amber-500 absolute left-3 top-2.5" />
                    <input
                      type="number"
                      min="0"
                      required={bulkFormData.updatePrice}
                      value={bulkFormData.creditCost}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, creditCost: Number(e.target.value) })}
                      className="w-full pl-9 pr-3 py-2 text-xs font-extrabold border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                      placeholder="Nhập 0 để đặt Miễn phí..."
                    />
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowBulkEditModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSavingBulk || (!bulkFormData.updateAuthor && !bulkFormData.updateSource && !bulkFormData.updatePrice)}
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center space-x-1.5"
                >
                  {isSavingBulk ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Đang cập nhật...</span>
                    </>
                  ) : (
                    <span>Cập nhật ({selectedIds.length} file)</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryManagement;
